<script lang="ts" setup>
import { computed } from 'vue';
import { useUserIdentifiers } from '@ilteducation/user-identifiers';

import { useLocale } from '../../composables/useLocale';
import useModal, { modals } from '../../composables/useModal';
import { useSidebarUser } from '../../composables/useSidebarUser';
import { useEscalateLogin } from '../../composables/useEscalateLogin';
import { useCurrentOrganization } from '../../composables/useCurrentOrganization';

import skolfederation from '../../icons/SkolFederation.vue';
import SidePanelSection from '../SidePanel/SidePanelSection.vue';
import SidePanelSectionItem from '../SidePanel/SidePanelSectionItem.vue';
import SidePanelSectionButtonItem from '../SidePanel/SidePanelSectionButtonItem.vue';
import { useUserContext } from '@ilteducation/ilt-auth';

const { t } = useLocale();
const { availableIdentifiers } = useUserIdentifiers();
const { user } = useSidebarUser();
const { organization } = useCurrentOrganization();
const { isMFA } = useUserContext();

const isEmployee = computed(
  () =>
    !!user.value?.associations.find(
      (assoc) => assoc.schoolUnit.uid === organization.value?.uid && ['admin', 'employee'].includes(assoc.role),
    ),
);

type PropIdentifier = { value: string; type: string };
type ExtendedIdentifier = PropIdentifier & { showCheck?: boolean; buttonMode?: boolean };

type Props = {
  identifiers: PropIdentifier[];
  username: string;
  qrCodeEnabled: boolean;
  isLoading: boolean;
};
const props = defineProps<Props>();

const getIconByType = (type: string) => {
  if (!availableIdentifiers.value.length) {
    return 'mdi-window-restore';
  }
  const img = availableIdentifiers.value.find((e) => e.type === type)?.iconDataUri || 'mdi-window-restore';

  if (img === 'mdi-window-restore') {
    if (type === 'username') {
      return 'mdi-account';
    } else if (type === 'qrcode') {
      return 'mdi-qrcode';
    } else if (type === 'password') {
      return 'mdi-lock';
    } else if (type === 'skolfederation') {
      return skolfederation;
    }
  }

  return img;
};

const validIdentifiers = computed((): ExtendedIdentifier[] => {
  const identifiers = [
    { type: 'username', value: props.username, showCheck: false, buttonMode: false },
    ...props.identifiers.filter((e) => !!e.value),
  ];

  if (!props.isLoading && props.qrCodeEnabled) {
    identifiers.push({
      type: 'qrcode',
      value: 'QR Code',
      showCheck: props.qrCodeEnabled,
      buttonMode: props.qrCodeEnabled,
    });
  }

  return identifiers;
});
const { open: openManageUserLoginInfo } = useModal(modals.MANAGE_USER_LOGIN_INFO);
const { requestEscalatedLogin } = useEscalateLogin();
const onSecureLoginClick = () => {
  requestEscalatedLogin();
};

const availableIdentifiersWithMFANotVisible = computed(() => {
  return availableIdentifiers.value
    .filter((id) => id.requiresMFA)
    .filter((id) => !validIdentifiers.value.find((vId) => vId.type === id.type));
});

const { open: openCreateLoginCardModal } = useModal(modals.CREATE_LOGIN_CARD);
const onItemClicked = (id: PropIdentifier) => {
  if (id.type === 'qrcode') {
    openCreateLoginCardModal({
      createQrCodeCheck: true,
      hideBackButton: true,
    });
  }
};
</script>

<template>
  <SidePanelSection :title="t('lcp.manage_login')" :loading="isLoading" :action="openManageUserLoginInfo">
    <v-row noGutters v-if="isLoading">
      <v-col>
        <v-skeleton-loader type="paragraph"></v-skeleton-loader>
      </v-col>
    </v-row>

    <v-row v-else class="d-flex flex-column ga-2" noGutters>
      <v-col v-for="id in validIdentifiers" :key="id.type + id.value">
        <SidePanelSectionItem
          v-if="!id.buttonMode"
          :text="id.value"
          :tooltipText="t(`lcp.${id.type}`)"
          :value="id.value"
          :icon="getIconByType(id.type)"
          :showCheck="id.showCheck || false"
        />
        <SidePanelSectionButtonItem
          v-else
          :text="id.value"
          :tooltipText="t(`lcp.${id.type}`)"
          :value="id.value"
          :icon="getIconByType(id.type)"
          :showCheck="id.showCheck || false"
          @click="() => onItemClicked(id)"
        />
      </v-col>
      <template v-if="isEmployee && !isMFA">
        <v-col v-for="id in availableIdentifiersWithMFANotVisible" :key="id.type">
          <SidePanelSectionItem
            :text="id.type"
            :tooltipText="t(`lcp.${id.type}`)"
            :value="id.type"
            :icon="getIconByType(id.type)"
          >
            <template v-slot:content>
              <v-btn variant="text" append-icon="mdi-lock" @click="onSecureLoginClick">
                {{ t('lcp.secure_login') }}
              </v-btn>
            </template>
          </SidePanelSectionItem>
        </v-col>
      </template>
    </v-row>
  </SidePanelSection>
</template>

<style lang="scss" scoped></style>
